import React, { useState, useEffect, useCallback } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  X, 
  Heart, 
  Share2, 
  Download, 
  Maximize2,
  Image as ImageIcon,
  Video as VideoIcon
} from 'lucide-react';
import toast from 'react-hot-toast';
import ImagePlaceholder from './ImagePlaceholder';

const isFilenameTitle = (title) => {
  if (!title) return true;
  const t = title.trim();
  if (t === '' || t === 'Untitled' || /^Photo\s+\d+$/i.test(t) || t === 'Gallery Media' || t === 'Gallery Photo') return true;
  if (/WhatsApp\s+Image/i.test(t)) return true;
  if (/^(IMG_|DSC_|PXL_|VID_|FILE_|\d{8}_\d{6})/i.test(t)) return true;
  if (/\.(jpg|jpeg|png|gif|webp|mp4|mov|avi|heic)$/i.test(t)) return true;
  return false;
};

const FeaturedGallery = ({ photos = [], title, description, className = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [likedItems, setLikedItems] = useState(new Set());
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Normalize photos to standard array format
  const items = (photos || []).map((item, idx) => ({
    id: item.id || `photo-${idx}`,
    title: item.title || item.caption || `Photo ${idx + 1}`,
    description: item.description || '',
    url: item.url || item.imageUrl || item.src || '',
    thumbnail: item.thumbnail || item.imageUrl || item.url || item.src || '',
    type: item.type || (item.url?.match(/\.(mp4|mov|avi)$/i) ? 'video' : 'image'),
    category: item.category || item.album || ''
  })).filter(item => item.url);

  // Ensure current index is valid if items length changes
  useEffect(() => {
    if (currentIndex >= items.length && items.length > 0) {
      setCurrentIndex(0);
    }
  }, [items.length, currentIndex]);

  const activeItem = items[currentIndex] || items[0];

  const handleNext = useCallback((e) => {
    if (e) e.stopPropagation();
    if (items.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % items.length);
  }, [items.length]);

  const handlePrev = useCallback((e) => {
    if (e) e.stopPropagation();
    if (items.length <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  // Touch swipe support
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    if (distance > minSwipeDistance) {
      handleNext();
    } else if (distance < -minSwipeDistance) {
      handlePrev();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (items.length <= 1) return;
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev, items.length]);

  const handleLike = (id, e) => {
    if (e) e.stopPropagation();
    const newLiked = new Set(likedItems);
    if (newLiked.has(id)) {
      newLiked.delete(id);
      toast.success('Removed from favorites');
    } else {
      newLiked.add(id);
      toast.success('Added to favorites');
    }
    setLikedItems(newLiked);
  };

  const handleShare = async (item, e) => {
    if (e) e.stopPropagation();
    try {
      if (navigator.share) {
        await navigator.share({
          title: item.title,
          url: item.url,
        });
      } else {
        await navigator.clipboard.writeText(item.url);
        toast.success('Image link copied to clipboard!');
      }
    } catch (err) {
      // Ignore share cancellation
    }
  };

  const handleDownload = (item, e) => {
    if (e) e.stopPropagation();
    try {
      const link = document.createElement('a');
      link.href = item.url;
      link.target = '_blank';
      link.download = `${(item.title || 'gallery_photo').replace(/\s+/g, '_')}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Download started!');
    } catch (err) {
      toast.error('Could not download image.');
    }
  };

  if (!items || items.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-200">
        <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium text-sm sm:text-base">No photos available in this gallery yet.</p>
      </div>
    );
  }

  return (
    <div className={`w-full space-y-6 sm:space-y-8 ${className}`}>
      {/* Title Header if provided */}
      {(title || description) && (
        <div className="text-center max-w-2xl mx-auto px-2">
          {title && <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{title}</h2>}
          {description && <p className="text-sm sm:text-base text-gray-600 mt-1">{description}</p>}
        </div>
      )}

      {/* Main Hero Carousel Container with Larger Vertical Height */}
      <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-gray-900 via-gray-950 to-red-950 border border-gray-800">
        {/* Ambient Blur Backdrop */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-40">
          {activeItem && (
            <img 
              src={activeItem.url} 
              alt="" 
              className="w-full h-full object-cover blur-2xl transform scale-125 filter saturate-150 transition-all duration-700" 
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/50" />
        </div>

        {/* Main Display Area - Expanded Vertical Height */}
        <div 
          className="relative min-h-[380px] sm:min-h-[500px] md:min-h-[600px] lg:min-h-[660px] flex items-center justify-center p-3 sm:p-6 md:p-8 cursor-pointer select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={() => setLightboxOpen(true)}
        >
          {activeItem.type === 'video' ? (
            <video
              src={activeItem.url}
              controls
              controlsList="nodownload"
              className="max-h-[340px] sm:max-h-[460px] md:max-h-[560px] lg:max-h-[620px] w-auto max-w-full object-contain rounded-xl sm:rounded-2xl shadow-2xl relative z-10"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="relative z-10 flex items-center justify-center w-full h-full">
              <ImagePlaceholder
                src={activeItem.url}
                alt={activeItem.title}
                className="max-h-[340px] sm:max-h-[460px] md:max-h-[560px] lg:max-h-[620px] w-auto max-w-full object-contain rounded-xl sm:rounded-2xl shadow-2xl transition-all duration-500"
                placeholderClassName="w-full h-[380px] sm:h-[480px] bg-gray-800 rounded-xl flex items-center justify-center"
              />
            </div>
          )}

          {/* Floating Left Arrow */}
          {items.length > 1 && (
            <button
              type="button"
              onClick={handlePrev}
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 sm:w-13 sm:h-13 bg-white/90 hover:bg-white text-gray-900 rounded-full flex items-center justify-center shadow-2xl backdrop-blur-md transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-orange-500"
              aria-label="Previous photo"
            >
              <ChevronLeft className="w-7 h-7 stroke-[2.5]" />
            </button>
          )}

          {/* Floating Right Arrow */}
          {items.length > 1 && (
            <button
              type="button"
              onClick={handleNext}
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 sm:w-13 sm:h-13 bg-white/90 hover:bg-white text-gray-900 rounded-full flex items-center justify-center shadow-2xl backdrop-blur-md transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-orange-500"
              aria-label="Next photo"
            >
              <ChevronRight className="w-7 h-7 stroke-[2.5]" />
            </button>
          )}

          {/* Fullscreen Expand Badge */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxOpen(true);
            }}
            className="absolute top-3 right-3 sm:top-5 sm:right-5 z-20 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-xl backdrop-blur-md transition-all duration-200 hover:scale-105"
            aria-label="Expand image"
          >
            <Maximize2 className="w-5 h-5" />
          </button>
        </div>

        {/* Carousel Footer & Dots Bar */}
        <div className="relative z-20 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-left w-full sm:w-auto truncate">
            {!isFilenameTitle(activeItem.title) && (
              <h3 className="text-white font-semibold text-base sm:text-lg truncate">{activeItem.title}</h3>
            )}
            {activeItem.description && !isFilenameTitle(activeItem.description) && (
              <p className="text-gray-300 text-xs sm:text-sm truncate">{activeItem.description}</p>
            )}
          </div>

          {/* Slide Indicator Dots */}
          {items.length > 1 && (
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-center py-1">
              {items.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentIndex(idx)}
                  className={`h-2.5 sm:h-3 rounded-full transition-all duration-300 ${
                    idx === currentIndex
                      ? 'w-6 sm:w-8 bg-orange-500 shadow-md shadow-orange-500/50'
                      : 'w-2.5 sm:w-3 bg-white/40 hover:bg-white/70'
                  }`}
                  aria-label={`Go to photo ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Photo Grid View (2-Column Mobile, Taller Vertical Cards, Clickable to Lightbox) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <span className="text-sm font-semibold text-gray-700">
            {items.length} {items.length === 1 ? 'Photo' : 'Photos'}
          </span>
          <span className="text-xs text-gray-500">Tap photo to view full size</span>
        </div>

        <div className="columns-2 sm:columns-3 md:columns-3 lg:columns-4 gap-3.5 sm:gap-4 md:gap-5 space-y-3.5 sm:space-y-4 md:space-y-5">
          {items.map((item, idx) => {
            const isSelected = idx === currentIndex;
            const isLiked = likedItems.has(item.id);
            return (
              <div
                key={item.id}
                onClick={() => {
                  setCurrentIndex(idx);
                  setLightboxOpen(true);
                }}
                className={`break-inside-avoid group relative rounded-2xl overflow-hidden cursor-pointer bg-gray-100 border-2 transition-all duration-300 shadow-md hover:shadow-2xl hover:-translate-y-1 ${
                  isSelected
                    ? 'border-orange-500 ring-4 ring-orange-500/30 shadow-lg'
                    : 'border-transparent hover:border-orange-400'
                }`}
              >
                <div className="relative w-full overflow-hidden bg-gray-100">
                  {item.type === 'video' ? (
                    <div className="relative w-full aspect-video bg-gray-900 overflow-hidden">
                      <video
                        src={item.url}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 pointer-events-none"
                        preload="metadata"
                        muted
                        playsInline
                      />
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-orange-500/90 text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                          <svg className="w-6 h-6 fill-current translate-x-0.5" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500 block rounded-xl"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjwvc3ZnPg==';
                      }}
                    />
                  )}

                  {/* Active highlight overlay */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-orange-500/10 pointer-events-none rounded-xl" />
                  )}

                  {/* Media Type Badge */}
                  {item.type === 'video' ? (
                    <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 backdrop-blur-sm z-10">
                      <VideoIcon className="w-3.5 h-3.5" />
                      <span>Video</span>
                    </div>
                  ) : null}

                  {/* Hover Action Controls */}
                  <div className="absolute bottom-2 right-2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                    <button
                      type="button"
                      onClick={(e) => handleLike(item.id, e)}
                      className={`p-2 rounded-full backdrop-blur-md shadow-md transition-transform hover:scale-110 ${
                        isLiked ? 'bg-red-500 text-white' : 'bg-white/90 text-gray-800 hover:bg-red-500 hover:text-white'
                      }`}
                      aria-label="Favorite photo"
                    >
                      <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-current' : ''}`} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleShare(item, e)}
                      className="p-2 rounded-full bg-white/90 hover:bg-orange-500 text-gray-800 hover:text-white backdrop-blur-md shadow-md transition-transform hover:scale-110"
                      aria-label="Share photo"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox Modal for Fullscreen View with Left & Right Arrows */}
      {lightboxOpen && activeItem && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-2 sm:p-4 backdrop-blur-md animate-fade-in select-none"
          onClick={() => setLightboxOpen(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close Lightbox Button */}
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 z-50 p-3 bg-white/20 hover:bg-white/30 text-white rounded-full transition-all backdrop-blur-md hover:scale-110 shadow-2xl"
            aria-label="Close modal"
          >
            <X className="w-6 h-6 stroke-[2.5]" />
          </button>

          {/* Modal Left Navigation Arrow */}
          {items.length > 1 && (
            <button
              type="button"
              onClick={handlePrev}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-50 w-12 h-12 sm:w-14 sm:h-14 bg-white/20 hover:bg-white/40 text-white rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-200 hover:scale-110 shadow-2xl border border-white/20"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-8 h-8 stroke-[2.5]" />
            </button>
          )}

          {/* Modal Right Navigation Arrow */}
          {items.length > 1 && (
            <button
              type="button"
              onClick={handleNext}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-50 w-12 h-12 sm:w-14 sm:h-14 bg-white/20 hover:bg-white/40 text-white rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-200 hover:scale-110 shadow-2xl border border-white/20"
              aria-label="Next image"
            >
              <ChevronRight className="w-8 h-8 stroke-[2.5]" />
            </button>
          )}

          {/* Lightbox Main Content - Taller Vertical Viewport */}
          <div 
            className="max-w-6xl w-full max-h-[92vh] flex flex-col items-center justify-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            {activeItem.type === 'video' ? (
              <video
                src={activeItem.url}
                controls
                autoPlay
                className="max-h-[82vh] w-auto max-w-full object-contain rounded-xl sm:rounded-2xl shadow-2xl"
              />
            ) : (
              <img
                src={activeItem.url}
                alt={activeItem.title}
                className="max-h-[82vh] w-auto max-w-full object-contain rounded-xl sm:rounded-2xl shadow-2xl transition-all duration-300"
              />
            )}

            {/* Modal Info & Action Bar */}
            <div className="mt-3 flex flex-col sm:flex-row items-center justify-between w-full max-w-3xl px-4 text-white gap-3 bg-black/40 backdrop-blur-md py-2.5 px-4 rounded-xl border border-white/10">
              <div className="text-center sm:text-left truncate">
                {!isFilenameTitle(activeItem.title) && (
                  <h4 className="font-bold text-base sm:text-lg truncate">{activeItem.title}</h4>
                )}
                {activeItem.description && !isFilenameTitle(activeItem.description) && (
                  <p className="text-xs text-gray-300 truncate">{activeItem.description}</p>
                )}
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={(e) => handleLike(activeItem.id, e)}
                  className={`p-2 rounded-full border border-white/20 backdrop-blur-md transition-colors ${
                    likedItems.has(activeItem.id) ? 'bg-red-500 text-white border-red-500' : 'hover:bg-white/20 text-white'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${likedItems.has(activeItem.id) ? 'fill-current' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={(e) => handleShare(activeItem, e)}
                  className="p-2 rounded-full border border-white/20 hover:bg-white/20 text-white backdrop-blur-md transition-colors"
                >
                  <Share2 className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={(e) => handleDownload(activeItem, e)}
                  className="p-2 rounded-full border border-white/20 hover:bg-white/20 text-white backdrop-blur-md transition-colors"
                >
                  <Download className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturedGallery;
